/* Keeping the app itself up to date (owner request 2026-10-13: the bookkeeper kept running an old version for an hour).
   - Which version runs here: built in (vite.config.ts) and shown at the bottom of Settings.
   - Which version is out: /version.json (never cached), asked when the app starts, when it is opened or shown again
     (also after the browser's back/forward cache), every 10 minutes while it is on screen, and on Refresh / pull down.
   - A newer one: the app reloads by itself only when nothing can be lost (lib/update.ts safeToReload: no window open,
     nobody typing, nothing waiting to be sent, no finger on the screen). Otherwise a small bar: "A new version is ready —
     Update". Changes still waiting to be sent hold the update back until they are saved.
   - Before reloading, the new page and its files are put in the offline copy (the service worker's cache), so the reload
     gets the new version even when the network is slow (the service worker answers from that copy after 4 s).
   - No loops: a reload that still shows the old version is not repeated at once (lib/update.ts mayAutoReload). */
import {parseVersion, safeToReload, tapBlock, mayAutoReload, noteTry, type AppVersion, type Moment, type Tries} from './lib/update.ts';

declare const __APP_VERSION__: AppVersion | undefined;
export const RUNNING: AppVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : {id: 'dev', commit: 'dev', built: '', label: 'dev'};
/** "Version a7e72b0 · Oct 13, 9:25 PM Central" (Settings) */
export const versionText = () => 'Version ' + RUNNING.label;

const TRIES = 'ot_update_tries', CHECK_EVERY = 10 * 60 * 1000;
let latest: AppVersion | null = null, wanted = false, applying = false;
const fingers = new Map<number, number>();   // pointers (fingers, mouse buttons) down right now -> since when
let app: {moment: () => Moment; flush: () => Promise<void>} | null = null;

function readTries(): Tries | null { try { return JSON.parse(sessionStorage.getItem(TRIES) || 'null'); } catch { return null; } }
function writeTries(t: Tries | null) { try { t ? sessionStorage.setItem(TRIES, JSON.stringify(t)) : sessionStorage.removeItem(TRIES); } catch { /* ignore */ } }
const moment = (): Moment => { const m = app!.moment(); return {...m, gesture: m.gesture || [...fingers.values()].some(t => Date.now() - t < 20000)}; };   // (a lost "let go" never blocks for long)

/** is a newer (or simply other: a rolled-back release counts too) version out? null: no, or not known (offline) */
export async function check(): Promise<AppVersion | null> {
  try { const r = await navigator.serviceWorker?.getRegistration(); if (r) void r.update().catch(() => {}); } catch { /* no service worker */ }
  try {
    const res = await fetch('/version.json', {cache: 'no-store'});
    if (res.ok) {
      let v: AppVersion | null = null; try { v = parseVersion(await res.json()); } catch { /* an older site answers with its page */ }
      if (v) latest = v.id !== RUNNING.id ? v : null;
    }
  } catch { /* offline: what was known stays */ }
  if (!latest) hideBar();
  return latest;
}

/** the new page and its files into the offline copy, so the reload cannot get the old saved page; false: the server
    still gives the old page (the new version is not everywhere yet) or it could not be reached */
async function prime(): Promise<boolean> {
  try {
    const mine = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')?.getAttribute('src') || '';
    const res = await fetch('/', {cache: 'no-store'});
    if (!res.ok) return false;
    const html = await res.text(), theirs = html.match(/\/assets\/index-[^"']+\.js/)?.[0] || '';
    if (mine && theirs && mine.endsWith(theirs)) return false;
    if ('caches' in window) {
      // the new page goes into the offline copy only together with ALL its files (owner report 2026-10-29: a saved page
      // whose files were missing opened offline as just the background and the logo). If a file cannot be had, the
      // offline copy stays the previous, complete one; the reload itself still goes ahead (online, the page asks the server).
      const names = (await caches.keys()).filter(k => k.startsWith('ot-shell-'));
      const files = [...new Set(html.match(/\/assets\/[^"'\s<>]+/g) || [])];
      for (const k of names) {
        const c = await caches.open(k);
        let complete = true;
        for (const f of files) {
          if (await c.match(f)) continue;
          try {
            const r = await fetch(f);
            // a file missing on the server is answered with the page: never keep that as the file
            if (!r.ok || (r.headers.get('content-type') || '').includes('text/html')) { complete = false; break; }
            await c.put(f, r);
          } catch { complete = false; break; }
        }
        if (complete) await c.put('/', new Response(html, {headers: {'content-type': 'text/html; charset=utf-8'}}));
      }
    }
    return true;
  } catch { return false; }
}
async function reloadTo(v: AppVersion): Promise<boolean> {
  if (applying) return true;
  applying = true;
  if (!(await prime())) { applying = false; return false; }
  writeTries(noteTry(readTries(), v.id, Date.now()));
  location.reload();
  return true;
}

/** after a check: reload by itself when that is safe (and not just tried), or show the bar */
async function consider(): Promise<void> {
  if (!app || applying || !navigator.onLine) return;   // offline: nothing to load, never a reload
  const v = await check(); if (!v) return;
  if (safeToReload(moment()) && mayAutoReload(readTries(), v.id, Date.now()) && await reloadTo(v)) return;
  showBar();
}

/** the person asked for it (the bar's Update, Refresh, pull down): send what waits first; a window open or changes that
    could not be sent hold it back (said in the bar). Returns what to tell them, or '' when no newer version is out. */
export async function applyNow(): Promise<string> {
  if (!app) return '';
  const v = await check(); if (!v) return '';   // asked afresh: an earlier answer may be out of date
  await app.flush();
  const block = tapBlock(moment());
  if (block) { wanted = block === 'pending'; showBar(); return barText(block); }
  wanted = false;
  if (await reloadTo(v)) return 'Loading the new version…';
  showBar(); return barText('notyet');
}
/** called on every status change: once the waiting changes are sent, an update the person asked for goes ahead */
export function poke() {
  if (wanted && app && !applying && !tapBlock(moment())) { wanted = false; void applyNow(); }
}

/* ---------- the bar ---------- */
function barText(why: '' | 'window' | 'pending' | 'notyet'): string {
  const n = app ? app.moment().pending : 0;
  return why === 'window' ? 'Save or close the open window first, then tap Update.'
    : why === 'pending' ? `${n} change${n === 1 ? ' is' : 's are'} still waiting to be sent. ${n === 1 ? 'It is' : 'They are'} kept on this device; the update follows once ${n === 1 ? 'it is' : 'they are'} saved.`
    : why === 'notyet' ? 'The new version is still on its way — try again in a minute.'
    : '';
}
function showBar() {
  let bar = document.getElementById('updateBar');
  if (!bar) {
    bar = document.createElement('div'); bar.id = 'updateBar'; bar.className = 'update-bar'; bar.setAttribute('role', 'status');
    bar.innerHTML = '<span class="ub-t">A new version is ready</span><button type="button" class="ub-go" id="updateGo">Update</button><span class="ub-why" id="updateWhy"></span>';
    document.body.appendChild(bar);
    bar.querySelector<HTMLButtonElement>('#updateGo')!.onclick = async () => {
      const msg = await applyNow();
      const why = document.getElementById('updateWhy'); if (why && msg && msg !== 'Loading the new version…') why.textContent = msg;
    };
  }
}
function hideBar() { document.getElementById('updateBar')?.remove(); wanted = false; }

/** start checking (main.ts) */
export function install(hooks: NonNullable<typeof app>) {
  app = hooks;
  // came back after reloading: the new version is running, or (the old one still) the next try waits (no loop)
  const t = readTries(); if (t && t.target === RUNNING.id) writeTries(null);
  addEventListener('pointerdown', ev => { fingers.set(ev.pointerId, Date.now()); }, true);
  const up = (ev: PointerEvent) => { fingers.delete(ev.pointerId); };
  addEventListener('pointerup', up, true); addEventListener('pointercancel', up, true);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState !== 'visible') fingers.clear(); });
  let last = 0;
  const again = () => { if (document.visibilityState === 'visible' && Date.now() - last > 5000) { last = Date.now(); void consider(); } };
  document.addEventListener('visibilitychange', again);
  addEventListener('focus', again);
  addEventListener('pageshow', ev => { if ((ev as PageTransitionEvent).persisted) last = 0; again(); });   // back/forward cache: an old page shown again
  setInterval(() => { if (document.visibilityState === 'visible') { last = Date.now(); void consider(); } }, CHECK_EVERY);
  last = Date.now(); void consider();
}
